/** @format */

import React, { useEffect, useState } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import PlatformMsisdnPoolNumber from '../../../interfaces/platform/PlatformMsisdnPoolNumber';
import Card from '../../../components/Card/Card';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import CardHeaderEx from '../../../components/Heading/CardHeaderExt';
import { ImportExport } from '@material-ui/icons';
import CardBody from '../../../components/Card/CardBody';
import GridItem from '../../../components/Grid/GridItem';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import SimCard from '@material-ui/icons/SimCard';
import MsisdnIccidParring from '../../msisdn-pools/component/MsisdnIccidParring';
import { MsisdnView } from './MsisdnView';
import { history } from '../../../core/history';
import MsisdnMnpHistory from './MsisdnMnpHistory';
import MsisdnPlkHistory from './MsisdnPlkHistory';
import MsisdnNotifyHistory from './MsisdnNotifyHistory';
import MsisdnImsiHistory from './MsisdnImsiHistory';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import { PlatformResellerLoader } from '../../../modules/platformreseller/PlatformResellerLoader';
import { numberStateIdMapInt } from '../../lib/search/MsisdnStateHelper';
import { PlatformIccidPoolNumber } from '../../../interfaces/platform/PlatformIccidPoolNumber';
import { PlatformIccidPoolNumberCollectionInterface } from '../../../interfaces/collections/PlatformIccidPoolNumberCollectionInterface';
import { iccidState } from '../../../helpers/iccidStatus';
import { ImsiSimCardParringOperation } from '../imsi/ImsiSimCardParringOperation';

type MsisdnDetailType = {
    handleSetBrandText: (text: string) => void;
};

export const MsisdnDetail = ({ handleSetBrandText }: MsisdnDetailType): React.ReactElement => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [platformReseller, setPlatformReseller] = useState<PlatformResellerCollectionInterface | null>(null);
    const [msisdn, setMsisdn] = useState<PlatformMsisdnPoolNumber | null>(null);
    const [iccid, setIccid] = useState<PlatformIccidPoolNumber | null>(null);

    const platformResellerLoader = new PlatformResellerLoader(new ItemLoaders(dispatch));

    useEffect(() => {
        const loadItems = (): void => {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getWithCallback(
                FormGeneralHelper.getByUri('/' + id),
                'msisdn_details_view_get',
                (data: PlatformMsisdnPoolNumber) => setMsisdn(data),
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        };
        if (!msisdn) {
            loadItems();
            handleSetBrandText('pools:msisdn.msisdn_number');
            return;
        }
    }, [dispatch, id, msisdn, handleSetBrandText]);

    useEffect(() => {
        const loadPlatformReseller = (): void => {
            platformResellerLoader.loadPlatformResellers({
                callback: (): void => {
                    setPlatformReseller(platformResellerLoader.getPlatformResellerCollection());
                },
                filters: [],
            });
        };
        if (!platformReseller) {
            loadPlatformReseller();
        } else {
            platformResellerLoader.setPlatformResellerCollection(platformReseller);
        }
    }, [dispatch, platformReseller, platformResellerLoader]);

    useEffect(() => {
        if (msisdn && msisdn.numberState === numberStateIdMapInt.PLANNED && msisdn.imsi) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_iccid_pool_numbers')),
                'storage_imsi_loader_items',
                [{ name: 'imsi', value: msisdn.imsi }],
                [],
                (items: PlatformIccidPoolNumberCollectionInterface): void => {
                    if (items['hydra:totalItems'] === 0) {
                        return;
                    }
                    setIccid(items['hydra:member'][0]);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, msisdn]);

    const goToImsiStorage = (item: { '@id': string }): void => {
        history.push(FormGeneralHelper.getRouteUrl('/iccid-view' + item['@id']));
    };

    const returnToList = (): void => {
        history.push(FormGeneralHelper.getRouteUrl('/msisdn-storage'));
    };

    const reloadPage = (): void => {
        setMsisdn(null);
        setIccid(null);
    };

    return (
        <GridContainer spacing={2}>
            <GridItem xs={12} md={6}>
                <Card>
                    {msisdn && (
                        <React.Fragment>
                            <CardHeader
                                color="info"
                                icon={<ImportExport />}
                                trans="pools:msisdn.msisdn_number"
                                transValueObject={{ msisdn: msisdn.msisdn }}
                            />
                            <CardBody>
                                <MsisdnView reload={(): void => setMsisdn(null)} item={msisdn} callBack={returnToList} />
                            </CardBody>
                        </React.Fragment>
                    )}
                </Card>
            </GridItem>
            {iccid && iccid.iccidState !== iccidState.USED && UserRoleHelper.checkRole(roles.ROLE_PLATFORM_SIMCARD_POST) && (
                <GridItem xs={6} md={6}>
                    <ImsiSimCardParringOperation imsiItem={iccid} reloadPage={reloadPage} />
                </GridItem>
            )}
            {msisdn && msisdn.numberState === 0 && UserRoleHelper.checkRole(roles.ROLE_PLATFORM_SIMCARD_POST) && (
                <GridItem xs={6} md={6}>
                    <Card>
                        <CardHeaderEx icon={<SimCard />} trans="simcard:add.msisdn_parring_with_sim_card" />
                        <CardBody>
                            <MsisdnIccidParring item={msisdn} refreshPage={goToImsiStorage} />
                        </CardBody>
                    </Card>
                </GridItem>
            )}
            {msisdn && (
                <React.Fragment>
                    {platformReseller && (
                        <React.Fragment>
                            <GridItem xs={12} md={12}>
                                <MsisdnImsiHistory msisdn={msisdn} />
                            </GridItem>
                            <GridItem xs={12} md={12}>
                                <MsisdnMnpHistory msisdn={msisdn} />
                            </GridItem>
                            {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_MSISDN_MNO_HISTORY) && (
                                <GridItem xs={12} md={12}>
                                    <MsisdnPlkHistory msisdn={msisdn} platformResellerCollection={platformReseller} />
                                </GridItem>
                            )}
                            {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_MSISDN_MNO_OPERATION) && (
                                <GridItem xs={12} md={12}>
                                    <MsisdnNotifyHistory msisdn={msisdn} platformResellerCollection={platformReseller} />
                                </GridItem>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </GridContainer>
    );
};
